import { useMemo } from 'react';
import { View } from 'react-native';
import Collapsible from 'react-native-collapsible';
import lodashIsUndefined from 'lodash/isUndefined';

import GcashSvg from '../Assets/payment_gcash.svg';
import GrabPaySvg from '../Assets/payment_grabpay.svg';
import MasterCardSvg from '../Assets/payment_mastercard.svg';
import McafeeSvg from '../Assets/payment_mcafee.svg';
import VisaSvg from '../Assets/payment_visa.svg';
import constants from '../Config/constants';
import CheckoutHelper from '../Helper/Checkout';
import useFormField from '../Hooks/useFormField';
import ThemeStyle from '../Theme/styles';

import ErrorPromptBelowInput from './FormFields/ErrorPromptBelowInput';
import PaymentMethodItem from './PaymentMethodItem';
import ResponsiveText from './ResponsiveText';
import TextInput from './TextInputDebounce';

const paymentMethods = [
  {
    label: 'Credit / Debit Card',
    value: 'card',
    images: [
      { image: McafeeSvg, width: 45, aspectRatio: 2.7 },
      { image: MasterCardSvg, width: 25, aspectRatio: 1.2 },
      { image: VisaSvg, width: 50, aspectRatio: 3.1 },
    ],
  },
  {
    label: 'Gcash',
    value: 'gcash',
    images: [{ image: GcashSvg, width: 75, aspectRatio: 4.1 }],
  },
  {
    label: 'Grabpay',
    value: 'grab_pay',
    images: [{ image: GrabPaySvg, width: 70, aspectRatio: 3.7 }],
  },
];

/**
 * Render Payment Methods
 * @typedef {undefined | {method: string}} Value
 * @typedef {(name: string) => (value: any) => void} InputChange
 * @param {{ error?: string; status?: string; value?: Value; onInputChange: InputChange; onMethodChange: (d: any) => void; }} param0 - Props needed for this component
 * @param {string} [param0.error] - Error string usually pass automatically when use the FormPaymentMethods
 * @param {string} [param0.errorCardNumber] - errorCardNumber string usually pass automatically when use the FormPaymentMethods
 * @param {string} [param0.errorExpiration] - errorExpiration string usually pass automatically when use the FormPaymentMethods
 * @param {string} [param0.errorCvv] - errorCvv string usually pass automatically when use the FormPaymentMethods
 * @param {string} [param0.status='success'] - Ui kitten status
 * @param {Value} [param0.value] - selected value
 * @param {InputChange} param0.onInputChange - a function that return a function to determine which card field change
 * @param {(d: any) => void} param0.onMethodChange - a function that return a new selected payment method
 * @returns A component that render a Payment Methods
 */
function PaymentMethods({
  error,
  errorCardNumber,
  errorExpiration,
  errorCvv,
  status = 'success',
  value,
  onInputChange,
  onMethodChange,
}) {
  const { getInputProps } = useFormField();

  const method = value?.paymentMethod || value?.method;

  const cardNumberValidation = useMemo(() => {
    if (lodashIsUndefined(value?.cardNumber)) return { isValid: true }; // untouched
    return CheckoutHelper.validateCardNumber(
      value.cardNumber,
      'This card number is not valid',
    );
  }, [value?.cardNumber]);

  const expiryValidation = useMemo(() => {
    if (
      lodashIsUndefined(value?.cardMonth) ||
      lodashIsUndefined(value?.cardYear)
    ) {
      // card month and card year must be touched to validate
      return { isValid: true };
    }
    return CheckoutHelper.validateCardExpiration(
      value.cardMonth,
      value.cardYear,
      'This expiration date is not valid',
    );
  }, [value?.cardMonth, value?.cardYear]);

  const cvvValidation = useMemo(() => {
    const errMsg = 'This cvv is not valid';
    if (lodashIsUndefined(value?.cardCvv)) return { isValid: true }; // untouched
    if (lodashIsUndefined(value?.cardNumber)) {
      // cardNumber is required and must be valid to verify the cvv
      return { isValid: false, message: errMsg };
    }
    return CheckoutHelper.validateCardCvv(
      value.cardNumber,
      value.cardCvv,
      errMsg,
    );
  }, [value?.cardNumber, value?.cardCvv]);

  const _onInputChange = (fieldName) => (value) =>
    onInputChange(fieldName)(value);

  const CardInput = useMemo(() => {
    const cardNumberError =
      (!cardNumberValidation.isValid && cardNumberValidation.message) ||
      errorCardNumber;
    const expirationError =
      (!expiryValidation.isValid && expiryValidation.message) ||
      errorExpiration;
    const cvvError =
      (!cvvValidation.isValid && cvvValidation.message) || errorCvv;
    return (
      <View style={ThemeStyle.spacingTopMedium}>
        <View style={ThemeStyle.spacingBottomMedium}>
          <TextInput
            label={(props) => (
              <ResponsiveText category="p2" style={props.style}>
                Card Number
              </ResponsiveText>
            )}
            direction="column"
            error={cardNumberError}
            type="cardnumber"
            placeholder="Card Number"
            autoComplete="cc-number"
            value={value?.cardNumber || ''}
            onChange={_onInputChange('cardNumber')}
            {...getInputProps(1)}
            withBorder
          />
          <ErrorPromptBelowInput text={cardNumberError} />
        </View>
        <View style={ThemeStyle.flexDirectionRowEndEnd}>
          {/* Exp Month */}
          <View style={ThemeStyle.flex1}>
            <TextInput
              type="number"
              label={(props) => (
                <ResponsiveText
                  category="p2"
                  style={props.style}
                  numberOfLines={1}
                >
                  Expiration Date
                </ResponsiveText>
              )}
              placeholder="MM"
              direction="column"
              error={expirationError}
              maxLength={2}
              autoComplete="cc-exp-month"
              value={value?.cardMonth || ''}
              onChange={_onInputChange('cardMonth')}
              {...getInputProps(2)}
              withBorder
            />
          </View>
          <View style={ThemeStyle.rowSeparator} />
          {/* Exp Year */}
          <View style={ThemeStyle.flex1}>
            <TextInput
              type="number"
              placeholder="YY"
              direction="column"
              error={expirationError}
              maxLength={2}
              autoComplete="cc-exp-year"
              value={value?.cardYear || ''}
              onChange={_onInputChange('cardYear')}
              {...getInputProps(3)}
              withBorder
            />
          </View>
          <View style={ThemeStyle.rowSeparator} />
          {/* CVV */}
          <View style={ThemeStyle.flex1}>
            <TextInput
              name="cardCvv"
              type="number"
              placeholder="CVC"
              direction="column"
              error={cvvError}
              maxLength={4}
              value={value?.cardCvv || ''}
              onChange={_onInputChange('cardCvv')}
              {...getInputProps(4, true)}
              secureTextEntry
              autoComplete="cc-csc"
              withBorder
            />
          </View>
        </View>
        {/* Error container for expiry and cvv */}
        <View style={ThemeStyle.flexDirectionRow}>
          <View style={[ThemeStyle.flex2, ThemeStyle.spacingRight]}>
            <ErrorPromptBelowInput text={expirationError} />
          </View>
          <View style={ThemeStyle.flex1}>
            <ErrorPromptBelowInput text={cvvError} />
          </View>
        </View>
      </View>
    );
  }, [
    cardNumberValidation,
    cvvValidation,
    errorCardNumber,
    errorCvv,
    errorExpiration,
    expiryValidation,
    value,
    _onInputChange,
  ]);

  return (
    <View style={ThemeStyle.flex1}>
      <View style={ThemeStyle.spacingBottomMedium}>
        <ResponsiveText category="p1">Payment Method</ResponsiveText>
      </View>

      {paymentMethods.map((pm, i) => {
        const isSelected = method === pm.value;
        const isCard = i === 0;
        const isLast = i === paymentMethods.length - 1;
        return (
          <View style={!isLast && ThemeStyle.spacingBottomMedium} key={i}>
            <PaymentMethodItem
              {...pm}
              checked={isSelected}
              status={status}
              onChange={() => onMethodChange(pm.value)}
            />
            {/* For card input */}
            {isCard && (
              <Collapsible
                collapsed={!isSelected}
                style={{ overflow: constants.isWeb ? 'clip' : 'hidden' }}
              >
                {CardInput}
              </Collapsible>
            )}
          </View>
        );
      })}

      <ErrorPromptBelowInput text={error} />
    </View>
  );
}

export default PaymentMethods;
